import React, { useState, useEffect } from 'react';
import { Box, Container, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, CircularProgress, Typography, Snackbar, Alert, Grid, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { galleryService } from '../services/api';
import { Add, Edit, Delete, Preview, Search, TrendingUp, Collections, Schedule } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../components/ConfirmDialog';

const GalleryPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, itemId: null, isDeleting: false });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at-desc');
  const [pageNumber, setPageNumber] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // Fetch data whenever search, sort, or page changes
  useEffect(() => {
    fetchData();
  }, [searchQuery, sortBy, pageNumber]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await galleryService.getAllGallery({
        page: pageNumber,
        limit: 10,
        search: searchQuery,
        sort: sortBy
      });

      let galleryData = [];
      let paginationData = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10
      };

      if (response.data?.data && Array.isArray(response.data.data)) {
        galleryData = response.data.data;
        paginationData = response.data.pagination || paginationData;
      } else if (Array.isArray(response.data)) {
        galleryData = response.data;
      }

      setItems(Array.isArray(galleryData) ? galleryData : []);
      setPagination(paginationData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setItems([]);
      setToast({ open: true, message: 'Error loading data', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    navigate('/admin/gallery/add');
  };

  const handleEditItem = (item) => {
    navigate(`/admin/gallery/${item.id}/edit`);
  };

  const handleDeleteItem = (id) => {
    setConfirmDialog({ open: true, itemId: id, isDeleting: false });
  };

  const handleConfirmDelete = async () => {
    try {
      setConfirmDialog(prev => ({ ...prev, isDeleting: true }));
      await galleryService.deleteGalleryItem(confirmDialog.itemId);
      setToast({ open: true, message: 'Gallery item deleted!', type: 'success' });
      setConfirmDialog({ open: false, itemId: null, isDeleting: false });
      setPageNumber(1);
      await fetchData();
    } catch (error) {
      setToast({ open: true, message: 'Error deleting item', type: 'error' });
      setConfirmDialog({ open: false, itemId: null, isDeleting: false });
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100%', p: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 1 }}>
                Gallery Management
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085' }}>
                Manage photos and videos
              </Typography>
            </Box>
            <Button 
              variant="contained" 
              startIcon={<Add />}
              onClick={handleAddItem}
              sx={{ backgroundColor: '#2c5f2d', '&:hover': { backgroundColor: '#1f4620' } }}
            >
              Add Media
            </Button>
          </Box>
        </Box>

        {/* Stats Summary */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 4 }}>
          {/* Total Items Card */}
          <Card sx={{ 
            p: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
            border: '1px solid #e8ecf1',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ backgroundColor: '#dcfce7', p: 2, borderRadius: '12px' }}>
                <TrendingUp sx={{ color: '#2c5f2d', fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                  Total Items
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2c5f2d', mt: 0.5 }}>
                  {pagination.totalItems}
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                  In gallery
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Photos Card */}
          <Card sx={{ 
            p: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
            border: '1px solid #e8ecf1',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ backgroundColor: '#e0e7ff', p: 2, borderRadius: '12px' }}>
                <Collections sx={{ color: '#4f46e5', fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                  Photos
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4f46e5', mt: 0.5 }}>
                  {items.filter(i => i.type === 'photo' || i.media_type === 'image').length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                  Image files
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Recent Uploads Card */}
          <Card sx={{ 
            p: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
            border: '1px solid #e8ecf1',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ backgroundColor: '#fef3c7', p: 2, borderRadius: '12px' }}>
                <Schedule sx={{ color: '#d97706', fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                  Recently Added
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#d97706', mt: 0.5 }}>
                  {items.filter(i => {
                    const itemDate = new Date(i.created_at);
                    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
                    return itemDate > thirtyDaysAgo;
                  }).length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                  Last 30 days
                </Typography>
              </Box>
            </Box>
          </Card>
        </Box>

        {/* Search and Filter Section */}
        <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                placeholder="Search by title or description..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPageNumber(1);
                }}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: '#667085' }} />,
                }}
                sx={{
                  backgroundColor: '#fff',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e8ecf1' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPageNumber(1);
                  }}
                  sx={{ backgroundColor: '#fff' }}
                >
                  <MenuItem value="created_at-desc">Date (Newest)</MenuItem>
                  <MenuItem value="created_at-asc">Date (Oldest)</MenuItem>
                  <MenuItem value="title-asc">Title (A-Z)</MenuItem>
                  <MenuItem value="title-desc">Title (Z-A)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: '#f8fafb', borderBottom: '2px solid #e8ecf1' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c', py: 2 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c', textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items && items.length > 0 ? (
                  items.map(item => (
                    <TableRow 
                      key={item.id}
                      sx={{ '&:hover': { backgroundColor: '#f8fafb' }, borderBottom: '1px solid #e8ecf1' }}
                    >
                      <TableCell sx={{ color: '#667085' }}>{item.id}</TableCell>
                      <TableCell sx={{ fontWeight: '500', color: '#1a202c' }}>{item.title}</TableCell>
                      <TableCell sx={{ color: '#667085' }}>{item.category || '-'}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Button 
                          size="small"
                          startIcon={<Preview />}
                          onClick={() => navigate(`/admin/gallery/${item.id}`)}
                          sx={{ color: '#059669', mr: 1 }}
                        >
                          View
                        </Button>
                        <Button 
                          size="small"
                          startIcon={<Edit />}
                          onClick={() => handleEditItem(item)}
                          sx={{ color: '#2c5f2d', mr: 1 }}
                        >
                          Edit
                        </Button>
                        <Button 
                          size="small"
                          startIcon={<Delete />}
                          onClick={() => handleDeleteItem(item.id)}
                          sx={{ color: '#dc2626' }}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4, color: '#667085' }}>
                      No gallery items found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Footer */}
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9fafb', borderTop: '1px solid #e8ecf1' }}>
            <Typography variant="body2" sx={{ color: '#667085' }}>
              Showing {items.length > 0 ? (pageNumber - 1) * pagination.itemsPerPage + 1 : 0} to {Math.min(pageNumber * pagination.itemsPerPage, pagination.totalItems)} of {pagination.totalItems} items
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button 
                variant="outlined" 
                size="small" 
                disabled={pageNumber === 1}
                onClick={() => setPageNumber(pageNumber - 1)}
              >
                Previous
              </Button>
              {[...Array(pagination.totalPages)].map((_, i) => (
                <Button
                  key={i + 1}
                  variant={pageNumber === i + 1 ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setPageNumber(i + 1)}
                  sx={{
                    minWidth: '36px',
                    backgroundColor: pageNumber === i + 1 ? '#059669' : 'transparent',
                    color: pageNumber === i + 1 ? '#fff' : '#667085',
                    borderColor: '#d5d9e2',
                    '&:hover': {
                      backgroundColor: pageNumber === i + 1 ? '#059669' : '#f3f4f6',
                      borderColor: '#667085'
                    }
                  }}
                >
                  {i + 1}
                </Button>
              ))}
              <Button 
                variant="outlined" 
                size="small"
                disabled={pageNumber === pagination.totalPages || pagination.totalPages === 0}
                onClick={() => setPageNumber(pageNumber + 1)}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Card>

        {/* Confirmation Dialog */}
        <ConfirmDialog
          open={confirmDialog.open}
          title="Delete Gallery Item"
          message="Are you sure you want to delete this gallery item? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          isDangerous={true}
          isLoading={confirmDialog.isDeleting}
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirmDialog({ open: false, itemId: null, isDeleting: false })}
        />

        {/* Toast Notification */}
        <Snackbar
          open={toast.open}
          autoHideDuration={6000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setToast({ ...toast, open: false })}
            severity={toast.type}
            sx={{ width: '100%' }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default GalleryPage;
